import React, { useState, useEffect } from 'react';
import { crawlingService } from '../../services/crawlingService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Loading from '../common/Loading';
import ConfirmationModal from '../common/ConfirmationModal';

const SocialMediaLinks = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [filters, setFilters] = useState({
    place_id: '',
    link_type: '',
    verification_status: '',
    platform_id: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
  });
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'default'
  });

  useEffect(() => {
    loadLinks();
    loadPlatforms();
  }, [filters, pagination.page]);

  const loadLinks = async () => {
    setLoading(true);
    const params = {
      page: pagination.page,
      page_size: pagination.page_size,
      ...filters,
    };

    // Remove empty filters
    Object.keys(params).forEach(key => {
      if (params[key] === '') delete params[key];
    });

    const { data, error } = await crawlingService.getSocialMediaLinks(params);
    
    if (data) {
      setLinks(data.results || []);
      setPagination(prev => ({
        ...prev,
        total: data.count || 0,
      }));
    } else if (error) {
      toast.error('Failed to load social media links');
      console.error('Error loading links:', error);
    }
    
    setLoading(false);
  };

  const loadPlatforms = async () => {
    const { data, error } = await crawlingService.getSocialMediaPlatforms();
    if (data) {
      setPlatforms(data);
    } else if (error) {
      console.error('Error loading platforms:', error);
    }
  };

  const handleSelectLink = (linkId) => {
    setSelectedLinks(prev => {
      if (prev.includes(linkId)) {
        return prev.filter(id => id !== linkId);
      } else {
        return [...prev, linkId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedLinks.length === links.length) {
      setSelectedLinks([]);
    } else {
      setSelectedLinks(links.map(link => link.id));
    }
  };

  const deleteSelectedLinks = async () => {
    if (selectedLinks.length === 0) {
      toast.error('Please select links to delete');
      return;
    }

    setConfirmationModal({
      isOpen: true,
      title: 'Delete Links',
      message: `Delete ${selectedLinks.length} selected link(s)?`,
      type: 'danger',
      onConfirm: async () => {
        const { data, error } = await crawlingService.deleteSocialMediaLinks(selectedLinks);
        
        if (data) {
          toast.success(`Deleted ${selectedLinks.length} link(s)`);
          setSelectedLinks([]);
          loadLinks();
        } else {
          toast.error('Failed to delete links');
        }
        
        setConfirmationModal({ ...confirmationModal, isOpen: false });
      }
    });
  };

  const verifyLink = async (linkId) => {
    // This would typically trigger a verification task
    toast.info('Link verification started');
  };

  const openLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getPlatformBadge = (link) => {
    if (link.platform) {
      return (
        <span 
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
          style={{ backgroundColor: crawlingService.getPlatformColor(link.link_type) }}
        >
          {crawlingService.getPlatformIcon(link.link_type)} {link.platform.display_name}
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {crawlingService.getPlatformIcon(link.link_type)} {link.link_type_display}
      </span>
    );
  };

  const getConfidenceBadge = (score) => {
    const percentage = (score * 100).toFixed(0);
    let color = 'bg-gray-100 text-gray-800';
    
    if (score >= 0.8) {
      color = 'bg-green-100 text-green-800';
    } else if (score >= 0.6) {
      color = 'bg-yellow-100 text-yellow-800';
    } else if (score >= 0.4) {
      color = 'bg-orange-100 text-orange-800';
    } else {
      color = 'bg-red-100 text-red-800';
    }
    
    return (
      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {percentage}% confidence
      </span>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.page_size);

  if (loading && links.length === 0) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
      />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold text-gray-900">Social Media Links</h1>
            <div className="flex items-center space-x-3">
              {selectedLinks.length > 0 && (
                <button
                  onClick={deleteSelectedLinks}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  Delete ({selectedLinks.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Link Type
              </label>
              <select
                value={filters.link_type}
                onChange={(e) => setFilters(prev => ({ ...prev, link_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Types</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="twitter">Twitter</option>
                <option value="linkedin">LinkedIn</option>
                <option value="youtube">YouTube</option>
                <option value="tiktok">TikTok</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="website">Website</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Verification Status
              </label>
              <select
                value={filters.verification_status}
                onChange={(e) => setFilters(prev => ({ ...prev, verification_status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Status</option>
                <option value="unverified">Unverified</option>
                <option value="verified">Verified</option>
                <option value="broken">Broken</option>
                <option value="redirected">Redirected</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Platform
              </label>
              <select
                value={filters.platform_id}
                onChange={(e) => setFilters(prev => ({ ...prev, platform_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Platforms</option>
                {platforms.map(platform => (
                  <option key={platform.id} value={platform.id}>
                    {platform.display_name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({ place_id: '', link_type: '', verification_status: '', platform_id: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Links List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* List Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={links.length > 0 && selectedLinks.length === links.length}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-500">
                {selectedLinks.length > 0 ? `${selectedLinks.length} selected` : 'Select all'}
              </span>
            </div>
            <div className="text-sm text-gray-500">
              Page {pagination.page} of {totalPages}
            </div>
          </div>

          {/* Links */}
          {links.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No social media links found</h3>
              <p className="text-gray-500">Links will appear here as places are crawled for social media information.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {links.map((link) => (
                <div key={link.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={selectedLinks.includes(link.id)}
                      onChange={() => handleSelectLink(link.id)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    
                    <div className="ml-4 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-base font-medium text-gray-900 flex items-center">
                            <span className="mr-2">{link.place_name}</span>
                            {getPlatformBadge(link)}
                            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${crawlingService.getVerificationStatusColor(link.verification_status)}`}>
                              {crawlingService.getVerificationStatusIcon(link.verification_status)} {link.verification_status_display}
                            </span>
                          </h4>
                          
                          <div className="mt-2 flex items-center space-x-4">
                            <button
                              onClick={() => openLink(link.url)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              {link.url.length > 60 ? `${link.url.substring(0, 60)}...` : link.url}
                            </button>
                            
                            {getConfidenceBadge(link.confidence_score)}
                          </div>
                          
                          {link.display_text && (
                            <p className="text-sm text-gray-600 mt-1">
                              Display text: "{link.display_text}"
                            </p>
                          )}
                        </div>
                        
                        <div className="ml-4 flex-shrink-0 text-right">
                          <div className="text-sm text-gray-500">
                            Found: {crawlingService.formatTimestamp(link.created_at)}
                          </div>
                          {link.response_code && (
                            <div className="text-sm text-gray-500">
                              Status: {link.response_code}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Link Details */}
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="space-y-1">
                          <div>Discovery method: {link.discovery_method}</div>
                          {link.found_on_url && (
                            <div>Found on: 
                              <button
                                onClick={() => openLink(link.found_on_url)}
                                className="text-blue-600 hover:underline ml-1"
                              >
                                {new URL(link.found_on_url).hostname}
                              </button>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          {link.follower_count && (
                            <div>Followers: {link.follower_count.toLocaleString()}</div>
                          )}
                          {link.is_verified_account && (
                            <div className="text-blue-600">✓ Verified Account</div>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          {link.final_url && link.final_url !== link.url && (
                            <div>
                              Final URL: 
                              <button
                                onClick={() => openLink(link.final_url)}
                                className="text-blue-600 hover:underline ml-1 text-xs"
                              >
                                {new URL(link.final_url).hostname}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {link.verification_status === 'unverified' && (
                            <button
                              onClick={() => verifyLink(link.id)}
                              className="px-3 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                            >
                              Verify
                            </button>
                          )}
                          
                          <button
                            onClick={() => openLink(link.url)}
                            className="px-3 py-1 bg-green-50 border border-green-200 text-green-700 rounded text-xs hover:bg-green-100 transition-colors"
                          >
                            Open Link
                          </button>
                          
                          <button
                            onClick={() => crawlingService.deleteSocialMediaLink(link.id).then(() => loadLinks())}
                            className="px-3 py-1 bg-red-50 border border-red-200 text-red-700 rounded text-xs hover:bg-red-100 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.page_size) + 1} to{' '}
                {Math.min(pagination.page * pagination.page_size, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                >
                  Previous
                </button>
                
                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + Math.max(1, pagination.page - 2);
                  if (page > totalPages) return null;
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 border rounded text-sm ${
                        page === pagination.page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialMediaLinks;